import ForeplayAPI from '@/api/foreplayServer'
import LENS_DEMO_FALLBACK_ADS from './lensDemoFallbackAds'
import { DEMO_AD_ACCOUNTS } from './lensDemoConstants'
import {
  DEMO_METRIC_KEYS,
  DEMO_SUM_METRIC_KEYS,
  DEMO_SCORE_METRIC_KEYS
} from './lensDemoMetricsRegistry'
import { buildAdMetrics, scaleMetrics, aggregateMetrics, applyRelativeMetrics, applyScores } from './lensDemoMetrics'

// Foreplay's production brand. pageId is the Facebook Ad Library id used by
// getBrandRankedAds. brandId is the unique-brands / discovery doc id used by
// getDiscoveryAds `orBrands`. If either fetch fails we fall back to the
// committed snapshot in lensDemoFallbackAds.js.
export const FOREPLAY_PAGE_ID = '108047081396228'
export const FOREPLAY_BRAND_ID = '108047081396228'

export const NAME_CONVENTIONS = [
  { prefix: 'Agency_', group: 'Agency' },
  { prefix: 'ExternalCreator_', group: 'External creator' },
  { prefix: 'InHouse_', group: 'Not agency' }
]

const DEFAULT_COMPARISON_GROUPS = [
  { group_by: 'Agency', query: [[{ key: 'ad_name', operation: 'contains', value: 'agency' }]] },
  { group_by: 'External creator', query: [[{ key: 'ad_name', operation: 'contains', value: 'creator' }]] },
  {
    group_by: 'Not agency',
    query: [
      [{ key: 'ad_name', operation: 'doesnt_contain', value: 'agency' }],
      [{ key: 'ad_name', operation: 'doesnt_contain', value: 'creator' }]
    ]
  }
]

let demoRowsPromise = null
let cachedDemoRows = null

function hashString (value) {
  const str = String(value || '')
  let hash = 2166136261
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function mulberry32 (seed) {
  let t = seed >>> 0
  return function random () {
    t += 0x6D2B79F5
    let r = Math.imul(t ^ (t >>> 15), t | 1)
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

function seededRange (random, min, max) {
  return min + (max - min) * random()
}

const DAY_MS = 24 * 60 * 60 * 1000

// Shared pools so grouping by headline / copy / landing page produces real
// groups, and so every metadata filter has something to match
const DEMO_CAMPAIGNS = ['Prospecting | Evergreen', 'Prospecting | Q4 Hooks', 'Retargeting | 30 Day', 'Brand | Always On']
const DEMO_ADSETS = ['Broad | US', 'Lookalike 1% | Purchasers', 'Interest | Creative Strategists', 'Retargeting | Site Visitors']
const DEMO_HEADLINES = ['Save every ad you see', 'Your swipe file, organized', 'See what competitors are running', 'Briefs your creators will love']
const DEMO_COPY = [
  'Stop screenshotting ads. Save them forever, with transcripts and landing pages.',
  'Track every ad your competitors launch, 24/7.',
  'Turn your best ads into briefs in minutes.',
  'Find winning hooks across 200M+ ads.',
  'Know which creatives actually carry your account.'
]
const DEMO_LANDING_PAGES = [
  'https://www.foreplay.co',
  'https://www.foreplay.co/swipe-file',
  'https://www.foreplay.co/spyder',
  'https://www.foreplay.co/lens',
  'https://www.foreplay.co/briefs'
]
const DEMO_CTA_TYPES = ['LEARN_MORE', 'SIGN_UP', 'SHOP_NOW', 'GET_STARTED']
// Roughly 1 in 5 demo ads is paused, so the status filter has both values
const PAUSED_PERCENT = 20

function pickSeeded (adId, salt, list) {
  return list[hashString(`${adId}:${salt}`) % list.length]
}

// Video only when the ad actually has a video, so the details drawer can play it
function normalizeAdType (ad) {
  if (ad.video) return 'video'
  const format = String(ad.display_format || ad.type || '').toLowerCase()
  return format.includes('carousel') ? 'carousel' : 'image'
}

function demoAdMetadata (ad, adId, createdAt) {
  const adType = normalizeAdType(ad)
  const isPaused = hashString(`${adId}:status`) % 100 < PAUSED_PERCENT
  return {
    campaign_name: pickSeeded(adId, 'campaign', DEMO_CAMPAIGNS),
    adset_name: pickSeeded(adId, 'adset', DEMO_ADSETS),
    ad_type: adType,
    ad_status: isPaused ? 'PAUSED' : 'ACTIVE',
    creative_distribution_format: hashString(`${adId}:distribution`) % 5 === 0 ? 'Dynamic Creative Ad' : 'Standard Ad',
    ad_created_at: createdAt,
    ad_title: pickSeeded(adId, 'headline', DEMO_HEADLINES),
    ad_copy: pickSeeded(adId, 'copy', DEMO_COPY),
    destination_url: pickSeeded(adId, 'landing', DEMO_LANDING_PAGES),
    creative_cta_type: pickSeeded(adId, 'cta', DEMO_CTA_TYPES),
    video_url_source: ad.video || null,
    video_url: ad.video || null,
    video_duration: adType === 'video' ? 12 + (hashString(`${adId}:duration`) % 64) : null
  }
}

function round (value, digits = 2) {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

function pickConvention (adId) {
  const index = hashString(adId) % NAME_CONVENTIONS.length
  return NAME_CONVENTIONS[index]
}

function applyNamingConvention (rawName, adId) {
  const name = (rawName || 'Untitled Ad').trim()
  if (NAME_CONVENTIONS.some((convention) => name.includes(convention.prefix.replace(/_$/, '')) || name.startsWith(convention.prefix))) {
    return name
  }
  const convention = pickConvention(adId)
  const stem = sanitizeStemForPrefix(name.replace(/\s+/g, '_'), convention.prefix)
  return `${convention.prefix}${stem}`
}

function pickAccount (adId) {
  const accounts = [
    { account_id: DEMO_AD_ACCOUNTS.META_US, account_name: 'Foreplay US' },
    { account_id: DEMO_AD_ACCOUNTS.META_PROSPECTING, account_name: 'Foreplay Prospecting' },
    { account_id: DEMO_AD_ACCOUNTS.TIKTOK, account_name: 'Foreplay TikTok' }
  ]
  return accounts[hashString(`${adId}:account`) % accounts.length]
}

function metricBandForAdName (adName) {
  const lower = String(adName || '').toLowerCase()
  if (lower.includes('agency')) return 'agency'
  if (lower.includes('creator')) return 'creator'
  return 'inhouse'
}

function isAgencyAdName (adName) {
  return metricBandForAdName(adName) === 'agency'
}

function isCreatorAdName (adName) {
  return metricBandForAdName(adName) === 'creator'
}

function isInHouseAdName (adName) {
  return metricBandForAdName(adName) === 'inhouse'
}

// Agency strongest, External creator middle, In-house weakest (conversions + ROAS)
const METRIC_BANDS = {
  agency: { conversionsMin: 40, conversionsMax: 220, roasMin: 2.4, roasMax: 5.4 },
  creator: { conversionsMin: 18, conversionsMax: 140, roasMin: 1.6, roasMax: 3.2 },
  inhouse: { conversionsMin: 4, conversionsMax: 80, roasMin: 0.8, roasMax: 2.2 }
}

export function assignMetrics (adId, adName, isVideo) {
  const random = mulberry32(hashString(adId))
  return buildAdMetrics(random, METRIC_BANDS[metricBandForAdName(adName)], isVideo)
}

function pickImage (ad) {
  return ad.thumbnail || ad.image || ad.ad_image_url || ad.video || ad.cards?.[0]?.resized_image_url || null
}

// Live Foreplay ads carry the brand ("Foreplay") as their name, which would
// collapse every row into a handful of identical ad names. Fetched ads get a
// generated ad name instead: a creative concept plus a running number, so
// each row (and its table selection key) is unique.
const DEMO_AD_CONCEPTS = ['Testimonial', 'Founder_Story', 'UGC_Review', 'Problem_Solution', 'Feature_Demo', 'Before_After', 'Listicle', 'Comparison', 'Offer', 'Tutorial']

function generatedAdName (adId, index) {
  const concept = pickSeeded(adId, 'concept', DEMO_AD_CONCEPTS)
  return `${concept}_${String(index + 1).padStart(2, '0')}`
}

function pickAdName (ad, adId, index) {
  if (Number.isInteger(index)) return generatedAdName(adId, index)
  return ad.name || ad.ad_name || ad.headline || ad.title || adId
}

// `index` is set for fetched ads (generated unique names); the committed
// snapshot keeps its own names
export function mapAdToDemoRow (ad, index) {
  const adId = String(ad.id || ad.ad_id)
  const adName = applyNamingConvention(pickAdName(ad, adId, index), adId)
  const imageUrl = pickImage(ad)
  const creativeHash = String(ad.creativeHash || ad.creative_hash || adId)
  const account = pickAccount(adId)
  const metrics = assignMetrics(adId, adName, !!ad.video)
  const now = Date.now()
  const daysLive = 6 + (hashString(`${adId}:days`) % 40)
  const minEventDate = new Date(now - daysLive * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const maxEventDate = new Date(now).toISOString().slice(0, 10)

  const metadata = demoAdMetadata(ad, adId, minEventDate)
  const adRecord = {
    ad_id: adId,
    ad_name: adName,
    ad_image_url: imageUrl,
    creative_hash: creativeHash,
    ad_status: metadata.ad_status,
    account_id: account.account_id,
    account_name: account.account_name
  }

  return {
    ...metrics,
    ...metadata,
    ...adRecord,
    group_by: adName,
    group_labels: [adName],
    ad_names: [adName],
    creative_hashes: [creativeHash],
    ads: [adRecord],
    active_ad_count: metadata.ad_status === 'ACTIVE' ? 1 : 0,
    paused_ad_count: metadata.ad_status === 'PAUSED' ? 1 : 0,
    inactive_ad_count: 0,
    min_event_date: minEventDate,
    max_event_date: maxEventDate
  }
}

function stripConventionPrefix (name) {
  const raw = String(name || '')
  for (const convention of NAME_CONVENTIONS) {
    if (raw.startsWith(convention.prefix)) {
      return raw.slice(convention.prefix.length)
    }
  }
  return raw
}

function sanitizeStemForPrefix (stem, prefix) {
  let clean = String(stem || 'Ad')
  if (prefix === 'Agency_') {
    // Agency group is contains "agency"; strip "creator" so it does not also
    // land in External creator.
    clean = clean.replace(/creator/gi, '')
  } else if (prefix === 'ExternalCreator_') {
    // Prefix already supplies "creator"; stem must not contain "agency".
    clean = clean.replace(/agency/gi, '').replace(/creator/gi, '')
  } else if (prefix === 'InHouse_') {
    // Not agency must match doesnt_contain agency AND doesnt_contain creator.
    clean = clean.replace(/agency/gi, '').replace(/creator/gi, '')
  }
  clean = clean.replace(/_+/g, '_').replace(/^_|_$/g, '')
  return clean || 'Ad'
}

function retagRow (row, prefix) {
  const stem = sanitizeStemForPrefix(stripConventionPrefix(row.ad_name), prefix)
  const adName = `${prefix}${stem}`
  const metrics = assignMetrics(row.ad_id, adName, row.ad_type === 'video')
  Object.assign(row, metrics, {
    ad_name: adName,
    group_by: adName,
    group_labels: [adName],
    ad_names: [adName]
  })
  if (row.ads?.[0]) {
    row.ads[0].ad_name = adName
  }
  return row
}

function ensureGroupCoverage (rows) {
  if (!rows.length) return rows

  if (!rows.some((row) => isAgencyAdName(row.ad_name))) {
    retagRow(rows[0], 'Agency_')
  }
  if (!rows.some((row) => isCreatorAdName(row.ad_name))) {
    const candidate = rows.find((row) => isInHouseAdName(row.ad_name)) ||
      rows.find((row, index) => index > 0 || rows.length === 1) ||
      rows[0]
    retagRow(candidate, 'ExternalCreator_')
  }
  if (!rows.some((row) => isInHouseAdName(row.ad_name))) {
    const agencyCount = rows.filter((row) => isAgencyAdName(row.ad_name)).length
    const creatorCount = rows.filter((row) => isCreatorAdName(row.ad_name)).length
    const candidate = rows.find((row) => {
      if (isAgencyAdName(row.ad_name)) return agencyCount > 1
      if (isCreatorAdName(row.ad_name)) return creatorCount > 1
      return true
    }) || rows[rows.length - 1]
    retagRow(candidate, 'InHouse_')
  }
  return rows
}

function normalizeFetchedAds (ads) {
  return ensureGroupCoverage(
    (ads || [])
      .filter((ad) => ad && (ad.id || ad.ad_id))
      .slice(0, 24)
      .map((ad, index) => mapAdToDemoRow(ad, index))
  )
}

async function fetchRankedForeplayAds () {
  const { data } = await ForeplayAPI.Brands.getBrandRankedAds(
    FOREPLAY_PAGE_ID,
    'all',
    { limit: 20, page: 0 }
  )
  return normalizeFetchedAds(data?.ads)
}

async function resolveForeplayBrandId () {
  if (FOREPLAY_BRAND_ID && FOREPLAY_BRAND_ID !== FOREPLAY_PAGE_ID) {
    return FOREPLAY_BRAND_ID
  }
  try {
    const { results } = await ForeplayAPI.Brands.getDiscoveryBrands(null, {
      textSearch: 'Foreplay'
    })
    const match = (results || []).find((brand) => {
      const name = (brand.name || brand.pageName || '').toLowerCase()
      return name === 'foreplay' || name.includes('foreplay')
    })
    return match?.id || match?.brandId || FOREPLAY_BRAND_ID
  } catch (error) {
    console.warn('[LensDemo] Could not resolve Foreplay brand id', error)
    return FOREPLAY_BRAND_ID
  }
}

async function fetchDiscoveryForeplayAds () {
  const brandId = await resolveForeplayBrandId()
  if (!brandId) return []
  const { results } = await ForeplayAPI.Ads.getDiscoveryAds(null, {
    orBrands: [brandId]
  }, true)
  return normalizeFetchedAds(results)
}

export async function loadDemoAdRows () {
  if (cachedDemoRows) return cachedDemoRows
  if (demoRowsPromise) return demoRowsPromise

  demoRowsPromise = (async () => {
    try {
      let rows = []
      try {
        rows = await fetchRankedForeplayAds()
      } catch (error) {
        console.warn('[LensDemo] Ranked ads fetch failed', error)
      }
      if (!rows.length) {
        try {
          rows = await fetchDiscoveryForeplayAds()
        } catch (error) {
          console.warn('[LensDemo] Discovery ads fetch failed', error)
        }
      }
      if (!rows.length) {
        rows = ensureGroupCoverage(LENS_DEMO_FALLBACK_ADS.map((ad) => mapAdToDemoRow(ad)))
      }
      cachedDemoRows = rows
      return rows
    } finally {
      demoRowsPromise = null
    }
  })()

  return demoRowsPromise
}

export function getCachedDemoAdRows () {
  return cachedDemoRows || ensureGroupCoverage(LENS_DEMO_FALLBACK_ADS.map((ad) => mapAdToDemoRow(ad)))
}

// Applied by scopeRows / the group lookup rather than per-row filter matching
const SCOPE_FILTER_KEYS = ['event_date', 'account_id', 'group_by']
const DATE_FILTER_KEYS = ['ad_created_at']

function startOfDay (time) {
  const date = new Date(time)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

// 'YYYY-MM-DD', 'today' or 'last_N_days' (the Lens date range format) → ms
function parseDemoDate (value) {
  if (value instanceof Date) return startOfDay(value)
  if (value === 'today') return startOfDay(Date.now())
  const relative = /^last_(\d+)_days$/.exec(String(value))
  if (relative) return startOfDay(Date.now() - Number(relative[1]) * DAY_MS)
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

function matchesDate (fieldValue, operation, value) {
  const field = parseDemoDate(fieldValue)
  if (field === null) return false
  if (operation === 'is_within_last') return field >= startOfDay(Date.now() - Number(value) * DAY_MS)
  const target = parseDemoDate(value)
  if (target === null) return true
  if (operation === 'greater_than') return field > target
  if (operation === 'less_than') return field < target
  if (operation === 'equals') return field === target
  return true
}

function matchesSingleFilter (row, filter) {
  const { key, operation, value } = filter || {}
  if (!key || SCOPE_FILTER_KEYS.includes(key)) return true
  // The demo Lens has no tags
  if (key === 'custom_tag') return operation === 'is_not'
  if (DATE_FILTER_KEYS.includes(key)) return matchesDate(row[key], operation, value)

  const field = row[key]
  const text = String(field ?? '').toLowerCase()
  const needle = String(value ?? '').toLowerCase()
  const isNumeric = typeof field === 'number' && !Number.isNaN(Number(value))

  switch (operation) {
    case 'contains':
      return text.includes(needle)
    case 'doesnt_contain':
      return !text.includes(needle)
    case 'equals':
    case 'is':
      return isNumeric ? field === Number(value) : text === needle
    case 'is_not':
      return isNumeric ? field !== Number(value) : text !== needle
    case 'greater_than':
      return isNumeric && field > Number(value)
    case 'less_than':
      return isNumeric && field < Number(value)
    case 'in':
      return Array.isArray(value) ? value.map(String).includes(String(field)) : text === needle
    default:
      return true
  }
}

export function rowMatchesFilters (row, filters = []) {
  return (filters || []).every((orGroup) => {
    if (!orGroup || !orGroup.length) return true
    const meaningful = orGroup.filter((filter) => filter?.key && !SCOPE_FILTER_KEYS.includes(filter.key))
    if (!meaningful.length) return true
    return meaningful.some((filter) => matchesSingleFilter(row, filter))
  })
}

function accountPlatform (accountId) {
  return accountId === DEMO_AD_ACCOUNTS.TIKTOK ? 'tiktok' : 'meta'
}

// The report's date range: event_date filters, or startDate/endDate (time series)
function resolveDateRange (query) {
  let start = query.startDate ? parseDemoDate(query.startDate) : null
  let end = query.endDate ? parseDemoDate(query.endDate) : null
  ;(query.filters || []).forEach((orGroup) => {
    const filter = orGroup?.[0]
    if (filter?.key !== 'event_date') return
    if (filter.operation === 'greater_than' && start === null) start = parseDemoDate(filter.value)
    if (filter.operation === 'less_than' && end === null) end = parseDemoDate(filter.value)
  })
  return start === null && end === null ? null : { start, end }
}

// Scale an ad's counts to the part of its live period inside the range (rates
// are re-derived); null when it wasn't live in the range at all
function scaleRowToRange (row, range) {
  const rowStart = parseDemoDate(row.min_event_date)
  const rowEnd = parseDemoDate(row.max_event_date)
  const overlapStart = Math.max(rowStart, range.start ?? rowStart)
  const overlapEnd = Math.min(rowEnd, range.end ?? rowEnd)
  if (overlapEnd < overlapStart) return null

  const factor = ((overlapEnd - overlapStart) / DAY_MS + 1) / ((rowEnd - rowStart) / DAY_MS + 1)
  return factor >= 1 ? row : { ...row, ...scaleMetrics(row, factor) }
}

// Rows in scope for a query: selected ad accounts and platform, scaled to the date range
export function scopeRows (rows, query = {}) {
  const accountIds = query.adAccountIds || []
  const range = resolveDateRange(query)
  return rows
    .filter((row) => !accountIds.length || accountIds.includes(row.account_id))
    .filter((row) => !query.platform || accountPlatform(row.account_id) === query.platform)
    .map((row) => (range ? scaleRowToRange(row, range) : row))
    .filter(Boolean)
}

function groupRows (rows, groupBy) {
  if (!groupBy || groupBy === 'ad_name' || groupBy === 'ad_id') {
    return rows.map((row) => ({
      ...row,
      group_by: groupBy === 'ad_id' ? row.ad_id : row.ad_name
    }))
  }

  // "Creative" groups by the creative hash; the group shows its first ad's name
  const field = groupBy === 'creative' ? 'creative_hash' : groupBy
  const groups = new Map()
  rows.forEach((row) => {
    const key = row[field] || row.ad_name
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(row)
  })

  return Array.from(groups.entries()).map(([key, members]) => {
    const merged = mergeRows(members, key)
    return groupBy === 'creative' ? { ...merged, ad_name: members[0].ad_name } : merged
  })
}

function mergeRows (members, groupKey) {
  const totals = computeTotals(members)
  const first = members[0]
  const ads = members.flatMap((row) => row.ads || [])
  return {
    ...first,
    ...totals.metrics,
    group_by: groupKey,
    ad_name: groupKey,
    ad_id: first.ad_id,
    ad_image_url: first.ad_image_url,
    creative_hash: first.creative_hash,
    ad_status: members.some((row) => row.ad_status === 'ACTIVE') ? 'ACTIVE' : 'PAUSED',
    ads,
    ad_names: members.map((row) => row.ad_name),
    group_labels: members.map((row) => row.ad_name),
    creative_hashes: members.map((row) => row.creative_hash),
    active_ad_count: members.reduce((sum, row) => sum + (row.active_ad_count || 0), 0),
    paused_ad_count: members.reduce((sum, row) => sum + (row.paused_ad_count || 0), 0),
    inactive_ad_count: 0,
    min_event_date: members.reduce((min, row) => (!min || row.min_event_date < min) ? row.min_event_date : min, first.min_event_date),
    max_event_date: members.reduce((max, row) => (!max || row.max_event_date > max) ? row.max_event_date : max, first.max_event_date)
  }
}

function computeTotals (rows) {
  return aggregateMetrics(rows)
}

// The previous period ran at ~82% of this period's volume
const PREVIOUS_PERIOD_FACTOR = 0.82

function attachDeviations (rows, averages) {
  return rows.map((row) => {
    const percentDeviation = {}
    DEMO_METRIC_KEYS.forEach((key) => {
      // Scores are percentiles already, so the server doesn't deviate them either
      if (DEMO_SCORE_METRIC_KEYS.includes(key)) return
      const avg = averages[key]
      const value = Number(row[key]) || 0
      percentDeviation[key] = avg ? round(((value - avg) / avg) * 100, 2) : 0
    })
    const previous = scaleMetrics(row, PREVIOUS_PERIOD_FACTOR)
    return {
      ...row,
      percent_deviation: percentDeviation,
      previous_period: {
        ...previous,
        weighted_averages: { goal_metric: previous.roas }
      }
    }
  })
}

function buildSummary (rows, totals) {
  const avg = {}
  const maxDev = {}
  const minDev = {}
  DEMO_METRIC_KEYS.forEach((key) => {
    const values = rows.map((row) => Number(row[key]) || 0)
    avg[key] = values.length ? round(values.reduce((acc, value) => acc + value, 0) / values.length, 2) : 0
    const deviations = rows.map((row) => row.percent_deviation?.[key] || 0)
    maxDev[key] = deviations.length ? Math.max(...deviations) : 0
    minDev[key] = deviations.length ? Math.min(...deviations) : 0
  })

  return {
    ad_groups: {
      avg,
      percent_deviation: { max: maxDev, min: minDev }
    }
  }
}

function sortRows (rows, sort) {
  const by = sort?.by || 'spend'
  const order = sort?.order === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const aVal = a[by] ?? a.group_by ?? ''
    const bVal = b[by] ?? b.group_by ?? ''
    if (typeof aVal === 'string' || typeof bVal === 'string') {
      return String(aVal).localeCompare(String(bVal)) * order
    }
    return ((Number(aVal) || 0) - (Number(bVal) || 0)) * order
  })
}

export function buildDefaultComparisonGroups () {
  return DEFAULT_COMPARISON_GROUPS.map((group) => ({
    group_by: group.group_by,
    query: group.query.map((orGroup) => orGroup.map((filter) => ({ ...filter })))
  }))
}

export function queryDemoGroups (rows, query = {}, reportType = 'top-performing') {
  const groupBy = query.group || query.groupBy || 'ad_name'
  const filters = query.filters || []
  const sort = query.sort || { by: 'spend', order: 'desc' }
  const page = Number(query.page) || 1
  const limit = Number(query.limit) || 10
  const isComparative = reportType === 'comparative'
  const customGroups = (query.comparativeQuery || []).filter((group) => group?.query?.length || group?.group_by)

  const filtered = scopeRows(rows, query).filter((row) => rowMatchesFilters(row, filters))

  let grouped
  if (isComparative && customGroups.length) {
    grouped = customGroups.map((group) => {
      const members = filtered.filter((row) => rowMatchesFilters(row, group.query))
      if (!members.length) {
        return {
          group_by: group.group_by,
          ad_name: group.group_by,
          ads: [],
          ad_names: [],
          group_labels: [],
          creative_hashes: [],
          active_ad_count: 0,
          paused_ad_count: 0,
          inactive_ad_count: 0,
          ...Object.fromEntries(DEMO_METRIC_KEYS.map((key) => [key, 0]))
        }
      }
      return mergeRows(members, group.group_by)
    })
  } else {
    grouped = groupRows(filtered, groupBy)
  }

  const totals = computeTotals(grouped.length ? grouped : filtered)
  const enriched = applyScores(applyRelativeMetrics(grouped, totals.sums))
  const withDeviation = attachDeviations(enriched, totals.metrics)
  const sorted = sortRows(withDeviation, sort)
  const start = (page - 1) * limit
  const pageRows = sorted.slice(start, start + limit)
  const summary = buildSummary(withDeviation, totals)

  return {
    data: {
      ad_groups: pageRows,
      summary_stats: summary,
      weighted_averages: totals.weighted,
      ...totals.sums,
      ...totals.weighted,
      active_ad_count: withDeviation.reduce((sum, row) => sum + (row.active_ad_count || 0), 0),
      paused_ad_count: withDeviation.reduce((sum, row) => sum + (row.paused_ad_count || 0), 0),
      inactive_ad_count: 0
    },
    pages: Math.max(1, Math.ceil(sorted.length / limit)),
    total: sorted.length
  }
}

// Breakdown drawer: the group plus every ad in it as a full row (metrics,
// metadata, deviation from the group average)
export function queryDemoGroupAds (rows, query = {}, reportType = 'top-performing') {
  const result = queryDemoGroups(rows, { ...query, page: 1, limit: 100 }, reportType)
  const groupKey = extractGroupKey(query)
  const group = result.data.ad_groups.find((row) => row.group_by === groupKey) || result.data.ad_groups[0]
  if (!group) return { data: { ad_groups: [], summary_stats: result.data.summary_stats } }

  const memberIds = new Set((group.ads || []).map((ad) => ad.ad_id))
  const members = scopeRows(rows, query).filter((row) => memberIds.has(row.ad_id))
  const groupTotals = computeTotals(members)
  const ads = attachDeviations(applyScores(applyRelativeMetrics(members, groupTotals.sums)), groupTotals.metrics)

  return {
    data: {
      ad_groups: [{
        ...group,
        ads,
        weighted_averages: { ...groupTotals.weighted, goal_metric: groupTotals.weighted.roas },
        previous_period: {
          ...group.previous_period,
          active_ad_count: Math.max(0, group.active_ad_count - 1)
        }
      }],
      summary_stats: result.data.summary_stats,
      weighted_averages: result.data.weighted_averages
    }
  }
}

function extractGroupKey (query) {
  const filters = query.filters || []
  for (const orGroup of filters) {
    const groupFilter = (orGroup || []).find((filter) => filter.key === 'group_by')
    if (groupFilter) return groupFilter.value
  }
  if (query.comparativeQuery?.[0]?.group_by) return query.comparativeQuery[0].group_by
  return null
}

const DEMO_THEMES = ['Productivity', 'Creative strategy', 'Competitive research', 'Team collaboration']
const DEMO_ANGLES = ['Save time', 'Stop losing great ads', 'Know what competitors run', 'Brief faster']
const DEMO_FUNNEL_STAGES = ['Awareness', 'Consideration', 'Conversion']
const DEMO_AGES = ['18-24', '25-34', '35-44']
const DEMO_GENDERS = ['Female', 'Male', 'All']
const DEMO_TARGETING = ['Media buyers and creative strategists at DTC brands', 'Agency teams managing paid social for clients', 'Founders running their own Meta ads']
const DEMO_EMOTIONS = ['Curiosity', 'Trust', 'Excitement', 'Relief', 'Confidence', 'Urgency']
const DEMO_TRANSCRIPT_LINES = [
  'You found a great ad yesterday. Today it is gone.',
  'Foreplay saves every ad you see, forever.',
  'Transcripts, landing pages and the creative itself, all in one place.',
  'Share boards with your team in one click.',
  'Try it free today.'
]

// Sample AI analysis: deterministic per ad so the drawer looks the same on every open
function buildDemoAiTransaction (row) {
  const random = mulberry32(hashString(`${row.ad_id}:ai`))
  const emotionalDriver = {}
  DEMO_EMOTIONS.forEach((emotion) => { emotionalDriver[emotion] = Math.round(seededRange(random, 25, 92)) })
  const lineLength = 3.2
  const segments = row.ad_type === 'video'
    ? DEMO_TRANSCRIPT_LINES.map((text, index) => ({ start: round(index * lineLength, 1), end: round((index + 1) * lineLength, 1), text }))
    : []
  return {
    transcription: { segments },
    theme: [pickSeeded(row.ad_id, 'theme', DEMO_THEMES)],
    ad_angle: [pickSeeded(row.ad_id, 'angle', DEMO_ANGLES)],
    funnel_stage: [pickSeeded(row.ad_id, 'funnel', DEMO_FUNNEL_STAGES)],
    persona: { age: pickSeeded(row.ad_id, 'age', DEMO_AGES), gender: pickSeeded(row.ad_id, 'gender', DEMO_GENDERS) },
    emotional_driver: emotionalDriver,
    product_category: 'Marketing software',
    creative_targeting: pickSeeded(row.ad_id, 'targeting', DEMO_TARGETING)
  }
}

// Ad details drawer: one ad (or creative) with metrics scoped to the report
// query, previous period, deviation from the account average and AI analysis
export function queryDemoCreative (rows, { adId, creativeHash }, query = {}) {
  const scoped = scopeRows(rows, query)
  const pool = scoped.length ? scoped : rows
  const row = pool.find((item) => {
    if (creativeHash) return item.creative_hash === creativeHash || item.creative_hashes?.includes(creativeHash)
    return item.ad_id === adId || item.ads?.some((ad) => ad.ad_id === adId)
  }) || pool[0]

  if (!row) return { data: {} }
  const poolTotals = computeTotals(pool)
  const ranked = applyScores(applyRelativeMetrics(pool, poolTotals.sums))
  const [withDeviation] = attachDeviations([ranked.find((item) => item.ad_id === row.ad_id) || row], poolTotals.metrics)
  return {
    data: {
      ...withDeviation,
      ai_transaction: buildDemoAiTransaction(row)
    }
  }
}

// Unique metrics for the ad details drawer, derived from the ad's own reach and clicks
export function buildDemoUniqueMetrics (row) {
  if (!row) return {}
  const { frequency } = row
  const reach = Math.max(1, row.reach)
  const uniqueClicks = Math.round(row.clicks * 0.86)
  const uniqueLinkClicks = Math.round(uniqueClicks * 0.78)
  const uniqueInlineLinkClicks = Math.round(uniqueClicks * 0.82)
  const perReach = (value) => round((value / reach) * 100, 2)
  const costPer = (value) => (value ? round(row.spend / value, 2) : 0)
  return {
    reach,
    frequency,
    cpmr: round((row.spend / reach) * 1000, 2),
    unique_clicks: uniqueClicks,
    unique_ctr: perReach(uniqueClicks),
    unique_inline_link_clicks: uniqueInlineLinkClicks,
    unique_inline_link_click_ctr: perReach(uniqueInlineLinkClicks),
    unique_link_clicks: uniqueLinkClicks,
    unique_link_clicks_ctr: perReach(uniqueLinkClicks),
    unique_outbound_clicks_ctr: perReach(Math.round(uniqueLinkClicks * 0.9)),
    cost_per_unique_click: costPer(uniqueClicks),
    cost_per_unique_inline_link_click: costPer(uniqueInlineLinkClicks)
  }
}

const DEMO_COMMENTS = [
  'This is exactly what our team needed.',
  'Does it work with TikTok ads too?',
  'We switched from screenshots to this last month. Game changer.',
  'How does the board sharing work?'
]

// A few sample comments per ad (authors are generic demo names)
export function buildDemoAdComments (adId) {
  const count = 2 + (hashString(`${adId}:comments`) % 3)
  return Array.from({ length: count }, (_, index) => ({
    author_name: `Demo viewer ${index + 1}`,
    text: DEMO_COMMENTS[(hashString(`${adId}:comment:${index}`)) % DEMO_COMMENTS.length],
    created_time: new Date(Date.now() - (index + 1) * 26 * 60 * 60 * 1000).toISOString(),
    like_count: hashString(`${adId}:likes:${index}`) % 24
  }))
}

export function buildDemoTimeSeries (rows, query = {}, interval = 1, isComparative = false) {
  const groupResult = queryDemoGroups(rows, {
    ...query,
    page: 1,
    limit: 100,
    comparativeQuery: isComparative ? query.comparativeQuery : []
  }, isComparative ? 'comparative' : 'top-performing')

  const selectedKeys = new Set(query.selectedRowKeys || [])
  const seriesRows = selectedKeys.size
    ? groupResult.data.ad_groups.filter((row) => selectedKeys.has(row.group_by) || selectedKeys.has(row.ad_id))
    : groupResult.data.ad_groups

  const metricKeys = (query.selectedColumns || ['spend', 'roas']).map((col) => (typeof col === 'string' ? col : col.key))
  const start = query.startDate ? new Date(query.startDate) : new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
  const end = query.endDate ? new Date(query.endDate) : new Date()
  const step = Math.max(1, Number(interval) || 1)
  const dateSegments = []
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  // cursor advances via setDate, which the lint rule can't see
  // eslint-disable-next-line no-unmodified-loop-condition
  while (cursor <= endDay) {
    dateSegments.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + step)
  }
  if (!dateSegments.length) dateSegments.push(formatDate(endDay))

  const maxMetrics = {}
  metricKeys.forEach((key) => { maxMetrics[key] = 0 })

  const data = seriesRows.map((row) => {
    const random = mulberry32(hashString(`${row.ad_id || row.group_by}:series`))
    const points = dateSegments.map((date, index) => {
      const nextDate = dateSegments[index + 1] || date
      const point = {
        event_date: date,
        interval_start: date,
        interval_end: nextDate
      }
      metricKeys.forEach((key) => {
        const base = Number(row[key]) || 0
        const wave = 0.55 + random() * 0.9
        let value = DEMO_SUM_METRIC_KEYS.includes(key)
          ? round((base / dateSegments.length) * wave, 2)
          : round(base * (0.75 + random() * 0.5), 2)
        // Scores are percentiles; keep the daily wobble inside 0-100
        if (DEMO_SCORE_METRIC_KEYS.includes(key)) value = Math.min(100, Math.max(0, Math.round(value)))
        point[key] = value
        if (value > maxMetrics[key]) maxMetrics[key] = value
      })
      return point
    })
    return {
      group_by: row.group_by,
      ad_id: row.ad_id,
      data: points,
      boundaries: {}
    }
  })

  return {
    date_segments: dateSegments,
    max_metrics: maxMetrics,
    data
  }
}

function formatDate (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function buildDemoInsights (rows) {
  const totals = computeTotals(rows)
  const topAds = sortRows(rows, { by: 'spend', order: 'desc' }).slice(0, 8)
  return {
    id: 'demo',
    last_refreshed_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    goal_metric: 'roas',
    currency: 'USD',
    insights: {
      spend: totals.sums.spend,
      roas: totals.weighted.roas,
      creatives_count: rows.length,
      ads: topAds,
      previous_period: {
        spend: round(totals.sums.spend * 0.84, 2),
        roas: round(totals.weighted.roas * 0.91, 2),
        creatives_count: Math.max(1, rows.length - 2)
      }
    },
    targets: null
  }
}

export {
  DEMO_METRIC_KEYS,
  DEMO_SUM_METRIC_KEYS
}
